import styled, { css, keyframes } from 'styled-components';

/* Palette follows the rest of the site (InfoSection, globalStyles):
   slate-navy ground, indigo #818cf8 accents, the #4b59f7 → #0467fb primary
   gradient. The ground here is one step deeper than #0f172a so it matches
   the near-black of the India network image and the art blends in. */
const ground = '#020617';
const indigo = '#818cf8';
const sky = '#38bdf8';
const heading = '#ffffff';
const body = '#cbd5e1';
const muted = '#94a3b8';
const line = 'rgba(148, 163, 184, 0.14)';

const reduced = '@media (prefers-reduced-motion: reduce)';

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-12px); }
`;

const breathe = keyframes`
  0%, 100% { opacity: 0.55; transform: scale(0.95); }
  50% { opacity: 1; transform: scale(1.05); }
`;

const ring = keyframes`
  0% { opacity: 0; transform: translate(-50%, -50%) scale(0.6); }
  30% { opacity: 0.7; }
  100% { opacity: 0; transform: translate(-50%, -50%) scale(1.35); }
`;

const ping = keyframes`
  0% { transform: scale(1); opacity: 0.8; }
  100% { transform: scale(3.2); opacity: 0; }
`;

const textGradient = css`
  background: linear-gradient(100deg, #ffffff 0%, #a5b4fc 50%, ${sky} 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
`;

export const AchievementSec = styled.section`
  position: relative;
  isolation: isolate;
  overflow: hidden;
  padding: 110px 0;
  background: ${ground};
  color: ${body};
  border-bottom: 1px solid rgba(255, 255, 255, 0.02);

  /* Faint blueprint grid, fading toward the edges. */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    z-index: -1;
    background-image: linear-gradient(to right, ${line} 1px, transparent 1px),
      linear-gradient(to bottom, ${line} 1px, transparent 1px);
    background-size: 56px 56px;
    -webkit-mask-image: radial-gradient(ellipse at center, #000 20%, transparent 75%);
    mask-image: radial-gradient(ellipse at center, #000 20%, transparent 75%);
  }

  /* Hairline of light along the top edge. */
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(to right, transparent, rgba(129, 140, 248, 0.7), transparent);
  }

  @media screen and (max-width: 768px) {
    padding: 72px 0;
  }
`;

/* Two soft colour fields behind the content. */
export const Glow = styled.div`
  position: absolute;
  z-index: -1;
  width: ${({ $size }) => $size};
  height: ${({ $size }) => $size};
  top: ${({ $top }) => $top};
  left: ${({ $left }) => $left};
  border-radius: 50%;
  background: ${({ $color }) => $color};
  filter: blur(90px);
  pointer-events: none;
`;

/* Scroll reveal: fades and rises once the element enters the viewport.
   Reduced motion keeps the fade and drops the movement. */
export const Reveal = styled.div`
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transform: ${({ $visible, $from }) =>
    $visible ? 'none' : $from === 'left' ? 'translateX(-32px)' : $from === 'right' ? 'translateX(32px)' : $from === 'scale' ? 'scale(0.96)' : 'translateY(24px)'};
  transition: opacity 0.6s cubic-bezier(0.22, 1, 0.36, 1) ${({ $delay = 0 }) => $delay}s,
    transform 0.6s cubic-bezier(0.22, 1, 0.36, 1) ${({ $delay = 0 }) => $delay}s;
  height: ${({ $fill }) => ($fill ? '100%' : 'auto')};

  ${reduced} {
    transform: none;
    transition: opacity 0.3s linear;
  }
`;

export const Intro = styled.div`
  max-width: 760px;
  margin: 0 auto;
  text-align: center;
`;

export const TopLine = styled.span`
  display: inline-block;
  padding: 7px 16px;
  border-radius: 999px;
  border: 1px solid rgba(129, 140, 248, 0.3);
  background: rgba(129, 140, 248, 0.1);
  color: ${indigo};
  font-family: 'Inter', sans-serif;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 1.5px;
  text-transform: uppercase;
`;

export const Heading = styled.h2`
  margin-top: 20px;
  font-family: 'Outfit', sans-serif;
  font-size: 2.75rem;
  line-height: 1.15;
  font-weight: 800;
  color: ${heading};

  @media screen and (max-width: 768px) {
    font-size: 1.9rem;
  }
`;

export const GradientText = styled.span`
  ${textGradient}
`;

export const Lead = styled.p`
  margin-top: 20px;
  font-family: 'Inter', sans-serif;
  font-size: 1.15rem;
  line-height: 1.7;
  color: ${body};

  @media screen and (max-width: 768px) {
    font-size: 1.02rem;
  }
`;

export const StoryRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 5fr) minmax(0, 7fr);
  align-items: center;
  gap: 64px;
  margin-top: 80px;

  @media screen and (max-width: 991px) {
    grid-template-columns: 1fr;
    gap: 56px;
    margin-top: 56px;
  }
`;

/* ---- Map ---------------------------------------------------------------- */

export const MapWrap = styled.div`
  position: relative;
  width: 100%;
  max-width: 416px;
  margin: 0 auto;

  @media screen and (max-width: 991px) {
    max-width: 340px;
  }
`;

export const MapGlow = styled.div`
  position: absolute;
  inset: 12%;
  border-radius: 50%;
  background: rgba(56, 189, 248, 0.25);
  filter: blur(48px);
  animation: ${breathe} 6s ease-in-out infinite;

  ${reduced} {
    animation: none;
  }
`;

export const MapRing = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 70%;
  aspect-ratio: 1 / 1;
  border-radius: 50%;
  border: 1px solid rgba(56, 189, 248, 0.3);
  opacity: 0;
  animation: ${ring} 4.8s ease-out ${({ $delay }) => $delay}s infinite;

  ${reduced} {
    display: none;
  }
`;

export const MapFloat = styled.div`
  position: relative;
  animation: ${float} 8s ease-in-out infinite;

  ${reduced} {
    animation: none;
  }
`;

export const MapImg = styled.img`
  display: block;
  width: 100%;
  height: auto;
  -webkit-mask-image: radial-gradient(ellipse at center, #000 58%, transparent 100%);
  mask-image: radial-gradient(ellipse at center, #000 58%, transparent 100%);
`;

export const Hub = styled.span`
  position: absolute;
  width: 8px;
  height: 8px;
  margin: -4px 0 0 -4px;
  border-radius: 50%;
  background: ${sky};
  box-shadow: 0 0 12px ${sky};

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background: ${sky};
    animation: ${ping} 2.4s ease-out ${({ $delay }) => $delay}s infinite;
  }

  ${reduced} {
    &::after {
      animation: none;
      opacity: 0;
    }
  }
`;

export const Chip = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(15, 23, 42, 0.72);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.4);
  animation: ${float} ${({ $duration }) => $duration}s ease-in-out ${({ $delay = 0 }) => $delay}s infinite;
  ${({ $placement }) =>
    $placement === 'top'
      ? css`
          top: 6%;
          right: -32px;
        `
      : css`
          bottom: 10%;
          left: -32px;
        `}

  @media screen and (max-width: 480px) {
    ${({ $placement }) => ($placement === 'top' ? 'right: -8px;' : 'left: -8px;')}
    padding: 8px 10px;
  }

  ${reduced} {
    animation: none;
  }
`;

export const IconBox = styled.span`
  display: grid;
  place-items: center;
  flex-shrink: 0;
  width: ${({ $size = 40 }) => $size}px;
  height: ${({ $size = 40 }) => $size}px;
  border-radius: 10px;
  background: ${({ $solid }) => ($solid ? 'linear-gradient(135deg, #4b59f7 0%, #0467fb 100%)' : 'rgba(129, 140, 248, 0.14)')};
  color: ${({ $solid }) => ($solid ? '#fff' : indigo)};
  font-size: ${({ $size = 40 }) => Math.round($size * 0.45)}px;
  box-shadow: ${({ $solid }) => ($solid ? '0 4px 15px rgba(75, 89, 247, 0.35)' : 'none')};
`;

export const ChipFigure = styled.span`
  display: block;
  font-family: 'Outfit', sans-serif;
  font-size: 1.15rem;
  font-weight: 800;
  line-height: 1.2;
  color: ${heading};
`;

export const ChipLabel = styled.span`
  display: block;
  max-width: 170px;
  font-family: 'Inter', sans-serif;
  font-size: 0.72rem;
  line-height: 1.3;
  color: ${muted};
`;

/* ---- Story -------------------------------------------------------------- */

export const SubHeading = styled.h3`
  font-family: 'Outfit', sans-serif;
  font-size: ${({ $small }) => ($small ? '1.6rem' : '2rem')};
  line-height: 1.2;
  font-weight: 800;
  color: ${heading};

  @media screen and (max-width: 768px) {
    font-size: ${({ $small }) => ($small ? '1.35rem' : '1.6rem')};
  }
`;

export const Text = styled.p`
  margin-top: ${({ $mt = 18 }) => $mt}px;
  font-family: 'Inter', sans-serif;
  font-size: ${({ $small }) => ($small ? '0.92rem' : '1.05rem')};
  line-height: 1.7;
  color: ${body};

  strong {
    color: ${heading};
    font-weight: 600;
  }
`;

export const TextLink = styled.a`
  color: ${indigo};
  font-weight: 600;
  text-decoration: underline;
  text-decoration-color: rgba(129, 140, 248, 0.4);
  text-underline-offset: 4px;
  transition: color 0.3s ease, text-decoration-color 0.3s ease;

  &:hover {
    color: #a5b4fc;
    text-decoration-color: #a5b4fc;
  }
`;

export const StatsLabel = styled.h4`
  margin-top: 40px;
  font-family: 'Inter', sans-serif;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: ${indigo};
`;

export const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  margin-top: 16px;

  @media screen and (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

export const StatCard = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 22px;
  overflow: hidden;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.06);
  background: linear-gradient(135deg, rgba(30, 41, 59, 0.7) 0%, rgba(15, 23, 42, 0.5) 100%);
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.3s ease, box-shadow 0.3s ease;

  &::before {
    content: '';
    position: absolute;
    top: -40px;
    right: -40px;
    width: 112px;
    height: 112px;
    border-radius: 50%;
    background: rgba(56, 189, 248, 0.18);
    filter: blur(28px);
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    transform: translateY(-4px);
    border-color: rgba(129, 140, 248, 0.45);
    box-shadow: 0 12px 40px -12px rgba(56, 189, 248, 0.45);
  }

  &:hover::before {
    opacity: 1;
  }

  ${reduced} {
    &:hover {
      transform: none;
    }
  }
`;

export const StatTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 48px;
`;

export const StatFigure = styled.span`
  font-family: 'Outfit', sans-serif;
  font-size: 2.3rem;
  font-weight: 800;
  line-height: 1;
  ${textGradient}

  @media screen and (max-width: 768px) {
    font-size: 2rem;
  }
`;

export const StatBigIcon = styled.span`
  display: inline-flex;
  font-size: 2.2rem;
  color: ${sky};
`;

/* ---- Challenge / Approach ---------------------------------------------- */

export const CardsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 20px;
  margin-top: 96px;

  @media screen and (max-width: 768px) {
    grid-template-columns: 1fr;
    margin-top: 64px;
  }
`;

export const StoryCard = styled.article`
  position: relative;
  height: 100%;
  padding: 36px;
  overflow: hidden;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.06);
  background: linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.35) 100%);

  @media screen and (max-width: 768px) {
    padding: 26px;
  }
`;

export const StepNumber = styled.span`
  position: absolute;
  top: -14px;
  right: 18px;
  font-family: 'Outfit', sans-serif;
  font-size: 6rem;
  font-weight: 800;
  line-height: 1;
  color: rgba(255, 255, 255, 0.04);
  pointer-events: none;
  user-select: none;
`;

/* ---- Closing ------------------------------------------------------------ */

export const Closing = styled.div`
  max-width: 780px;
  margin: 96px auto 0;
  text-align: center;

  @media screen and (max-width: 768px) {
    margin-top: 64px;
  }
`;

export const QuoteMark = styled.span`
  display: inline-flex;
  margin-top: 48px;
  font-size: 1.9rem;
  color: rgba(56, 189, 248, 0.6);
`;

export const Quote = styled.blockquote`
  margin-top: 16px;
  font-family: 'Outfit', sans-serif;
  font-size: 2.2rem;
  font-weight: 800;
  line-height: 1.3;
  ${textGradient}

  @media screen and (max-width: 768px) {
    font-size: 1.55rem;
  }
`;

export const Pillars = styled.ul`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
  margin-top: 40px;
  list-style: none;
`;

export const Pillar = styled.li`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 18px;
  border-radius: 999px;
  border: 1px solid rgba(129, 140, 248, 0.3);
  background: rgba(129, 140, 248, 0.08);
  font-family: 'Inter', sans-serif;
  font-size: 0.9rem;
  font-weight: 600;
  font-style: italic;
  color: ${heading};

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${sky};
    box-shadow: 0 0 8px ${sky};
  }
`;
